import {
  ChartAdapter,
  ChartBuildContext,
  EChartsOption,
  GaugeChartData,
  GaugeChartSettings,
} from "../types";

import { buildLegend, buildTooltip, mergeGaugeSettings } from "./shared";

function resolveGaugeValueRange(
  data: GaugeChartData,
  settings: Partial<GaugeChartSettings>
) {
  let min =
    settings.min !== undefined ? settings.min : Number.POSITIVE_INFINITY;
  let max =
    settings.max !== undefined ? settings.max : Number.NEGATIVE_INFINITY;

  if (settings.min === undefined || settings.max === undefined) {
    data.series.forEach((series) => {
      if (settings.min === undefined && series.value < min) {
        min = series.value;
      }
      if (settings.max === undefined && series.value > max) {
        max = series.value;
      }
    });
  }

  if (settings.min === undefined && min === Number.POSITIVE_INFINITY) {
    min = 0;
  }
  if (settings.max === undefined && max === Number.NEGATIVE_INFINITY) {
    max = 100;
  }

  if (min === max) {
    max = min + 1;
  }

  return { min, max };
}

const gaugeChartAdapter: ChartAdapter<"gauge"> = {
  type: "gauge",
  buildOption: (ctx: ChartBuildContext<"gauge">) => {
    const { data } = ctx;
    const settings = mergeGaugeSettings(ctx.settings);

    const legend = buildLegend(data.legend, settings.legend);
    const tooltip = buildTooltip(data.tooltip, {
      trigger: settings.tooltip?.trigger ?? "item",
      enable: settings.tooltip?.enable,
      formatter: settings.tooltip?.formatter,
    });

    const { min, max } = resolveGaugeValueRange(data, settings);

    const series = data.series.map((seriesItem) => {
      const style = settings.style ?? "basic";
      const color = seriesItem.color ?? "#5470C6";

      type GaugeStyle = NonNullable<GaugeChartSettings["style"]>;

      const styleDefaults: Record<
        GaugeStyle,
        {
          startAngle?: number;
          endAngle?: number;
          splitNumber?: number;
          progress?:
            | false
            | {
                width?: number;
              };
          pointer?:
            | false
            | {
                length?: string;
                width?: number;
                color?: string;
              };
          axisLine?: {
            width?: number;
            color?: [number, string][];
          };
          detail?: {
            fontSize?: number;
            offsetCenter?: [number | string, number | string];
          };
        }
      > = {
        basic: {
          startAngle: 225,
          endAngle: -45,
          splitNumber: 10,
          progress: false,
          pointer: {
            length: "70%",
            width: 4,
          },
          axisLine: {
            width: 6,
            color: [
              [1, color],
            ],
          },
          detail: { fontSize: 18, offsetCenter: [0, "70%"] },
        },
        simple: {
          startAngle: 225,
          endAngle: -45,
          splitNumber: 12,
          progress: { width: 12 },
          pointer: {
            length: "75%",
            width: 5,
          },
          axisLine: {
            width: 12,
            color: [
              [1, color],
            ],
          },
          detail: { fontSize: 28, offsetCenter: [0, "70%"] },
        },
        speed: {
          startAngle: 210,
          endAngle: -30,
          splitNumber: 8,
          progress: { width: 18 },
          pointer: {
            length: "85%",
            width: 6,
          },
          axisLine: {
            width: 18,
            color: [
              [0.75, color],
              [1, "#E0E0E0"],
            ],
          },
          detail: { fontSize: 36, offsetCenter: [0, "60%"] },
        },
        stageSpeed: {
          startAngle: 210,
          endAngle: -30,
          splitNumber: 6,
          progress: false,
          pointer: {
            length: "78%",
            width: 6,
          },
          axisLine: {
            width: 18,
            color: [
              [0.3, "#57D1F7"],
              [0.7, "#7CFFB2"],
              [1, "#FF6E76"],
            ],
          },
          detail: { fontSize: 24, offsetCenter: [0, "65%"] },
        },
      };

      const defaults = styleDefaults[style] ?? styleDefaults.basic;

      const startAngle = settings.startAngle ?? defaults.startAngle ?? 225;
      const endAngle = settings.endAngle ?? defaults.endAngle ?? -45;
      const splitNumber =
        settings.splitNumber ?? defaults.splitNumber ?? 10;

      const progressEnabled =
        settings.showProgress !== undefined
          ? settings.showProgress
          : defaults.progress !== false;

      const pointerEnabled =
        settings.showPointer !== undefined
          ? settings.showPointer
          : defaults.pointer !== false;

      const axisLineColor =
        defaults.axisLine?.color ?? ([
          [1, color],
        ] as [number, string][]);

      const axisLineWidth = defaults.axisLine?.width ?? 10;

      return {
        type: "gauge",
        name: seriesItem.name,
        min: settings.min ?? min,
        max: settings.max ?? max,
        startAngle,
        endAngle,
        splitNumber,
        axisLine: {
          lineStyle: {
            width: axisLineWidth,
            color: axisLineColor,
          },
        },
        progress: progressEnabled
          ? {
              show: true,
              width:
                (defaults.progress && defaults.progress.width) ?? axisLineWidth,
              itemStyle: {
                color,
              },
            }
          : undefined,
        pointer: pointerEnabled
          ? defaults.pointer
            ? {
                show: true,
                length: defaults.pointer.length ?? "70%",
                width: defaults.pointer.width,
                itemStyle: defaults.pointer.color
                  ? { color: defaults.pointer.color }
                  : undefined,
              }
            : undefined
          : { show: false },
        axisLabel: {
          formatter: "{value}",
        },
        detail: {
          valueAnimation: true,
          formatter:
            seriesItem.detailFormatter ??
            ((value: number) => value.toFixed(0)),
          color,
          fontSize: defaults.detail?.fontSize,
          offsetCenter:
            defaults.detail?.offsetCenter ??
            ([0, "65%"] as [number | string, number | string]),
        },
        data: [
          {
            value: seriesItem.value,
            name: seriesItem.name,
          },
        ],
      };
    });

    const option: EChartsOption = {
      legend,
      tooltip,
      series,
    };

    return option;
  },
  getDefaultSettings: (): Partial<GaugeChartSettings> => ({
    legend: { show: false },
    tooltip: { enable: true, trigger: "item" },
    style: "basic",
    min: 0,
    max: 100,
    startAngle: 225,
    endAngle: -45,
    splitNumber: 10,
    showPointer: true,
    showProgress: true,
  }),
};

export default gaugeChartAdapter;

